import {
  Col,
  Input,
  message,
  Modal,
  ModalProps,
  Row,
  Typography,
  Tabs,
} from 'antd';
import { useIntl } from 'react-intl';
import ModuleTenderHammerNoColor from '~src/assets/nav-icons/folder-tender-hammer-no-color.svg';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { Fragment, useEffect, useRef, useState } from 'react';
import { FieldSelector } from '~components/popups-modals/DetailsLayouts/FieldSelector/FieldSelector';
import IconButton from '~components/_common/IconButton/IconButton';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { RegistrationLabelValuesVM } from '~src/types/db';
import { queryCache, useMutation } from 'react-query';
import { editTendersLabel, editTendersLabels } from '~src/services/api';
import { appLog } from '~src/utils/log';
import { useRegistrationLabelGroups } from '~src/hooks/api-hooks/useRegistrationLabelGroups';
// Make sure this path points to where your LabelsTab component is located
import { LabelsTab } from '../ProjectSettingsModal/RightsModal/Tabs/LabelsTab';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { useProject } from '~src/hooks/api-hooks/useProject';
// Adjust the import path for useProject if necessary (e.g., '~src/hooks/api-hooks/useProject')

const { TabPane } = Tabs;

export const LabelsModalWindow = (
  props: Pick<ModalProps, 'visible' | 'onOk' | 'onCancel'> & {
    item: {
      id?: string;
      reference?: string;
      title?: string;
      folderId?: string;
      projectId?: string;
      tenderId?: string;
      label_values: RegistrationLabelValuesVM[];
    };
  }
) => {
  const intl = useIntl();
  const style = useStyles(styleFn);
  const archiveSettings = useArchiveSettings();

  // Fetch Project Data to determine permissions
  const projectDataFetch = useProject();
  const projectData = projectDataFetch.data;

  const [activeTab, setActiveTab] = useState('values');
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(0);

  const [isModalLoading, setIsModalLoading] = useState(false);

  // Logic to show/hide the Settings tab
  const isTenderingOn =
    archiveSettings.data?.resArchiveSettings?.is_tendering_on;
  const isTenderAdmin = projectData?.is_tender_admin;

  const showSettingsTab = isTenderingOn || isTenderAdmin;

  // We refetch groups for the selector (Used in Tab 1)
  // Destructure 'refetch' to manually update list when switching tabs
  const {
    data: labelsData,
    refetch: refetchLabels,
  } = useRegistrationLabelGroups(false);

  // Effect: Refetch groups when switching back to 'values' tab
  // This ensures groups restored/deleted in 'settings' appear immediately in the selector
  useEffect(() => {
    if (activeTab === 'values') {
      refetchLabels();
    }
  }, [activeTab, refetchLabels]);

  const getLabelValues = (labelValues) => {
    return labelValues.reduce((acc, group) => {
      acc[group.group_id] = group.labels.map((label) => label.label_id);
      return acc;
    }, {});
  };

  const [inputValues, setInputValues] = useState(() => {
    const defaultValues = {};
    props.item.label_values.forEach((group) => {
      group.labels.forEach((label) => {
        defaultValues[label.label_id] = label.value ?? '';
      });
    });
    return defaultValues;
  });

  const [selectedLabels, setSelectedLabels] = useState<
    Record<number, number[]>
  >(() => getLabelValues(props.item.label_values));

  const [defaultSelectedLabels] = useState<Record<number, number[]>>(() =>
    getLabelValues(props.item.label_values)
  );

  // Init once per "open session", not on every refetch
  const didInitRef = useRef(false);

  // Only reset form when modal opens, not when data refetches
  useEffect(() => {
    // When modal closes -> allow init next time it opens
    if (!props.visible) {
      didInitRef.current = false;
      setActiveTab('values'); // Reset to first tab on close
      return;
    }

    // When modal is visible -> initialize ONLY ONCE for this open session
    if (props.visible && !didInitRef.current) {
      const nextInputValues = {};
      props.item.label_values.forEach((group) => {
        group.labels.forEach((label) => {
          nextInputValues[label.label_id] = label.value ?? '';
        });
      });

      setInputValues(nextInputValues);
      setSelectedLabels(getLabelValues(props.item.label_values));

      didInitRef.current = true;
    }
  }, [props.visible, props.item.label_values]);

  const [doUpdateLabels] = useMutation(editTendersLabels, {
    onMutate: () => {
      setIsModalLoading(true);
    },
    onError: (err) => {
      appLog('err -> ', err);
      message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
      setIsModalLoading(false);
    },
    onSuccess: () => {
      message.success(intl.formatMessage({ id: 'changes-saved-successfully' }));
      queryCache.invalidateQueries('tendersRegistrationCoreTenders');
      setIsModalLoading(false);
    },
  });

  const handleChange = (label: number, value: string) => {
    setInputValues((prevState) => ({
      ...prevState,
      [label]: value,
    }));
  };

  const handleSave = async () => {
    if (!props.item.projectId || !props.item.tenderId) return;

    const data: { value: unknown; label_id: number }[] = Object.entries(
      inputValues
    ).map(([key, value]) => {
      return { label_id: +key, value: value ?? '' };
    });

    await doUpdateLabels({
      projectId: props.item.projectId,
      tenderId: props.item.tenderId,
      data,
    });

    props.onOk &&
      props.onOk(
        (null as unknown) as React.MouseEvent<HTMLElement, MouseEvent>
      );
  };

  const Title = () => (
    <div>
      <NavIcon
        customStyle={{ width: '45px', height: '22px' }}
        icon={ModuleTenderHammerNoColor}
      />
      {intl.formatMessage({ id: 'tender-details-custom-fields' })}
    </div>
  );

  const ConfirmDeleteModal = () => {
    const [isConfirmLoading, setIsConfirmLoading] = useState(false);

    const [doUpdateLabel] = useMutation(editTendersLabel, {
      onMutate: () => {
        setIsConfirmLoading(true);
      },
      onError: (err) => {
        appLog('err -> ', err);
        message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
        setIsConfirmLoading(false);
      },
      onSuccess: (data, variables) => {
        message.success(
          intl.formatMessage({ id: 'changes-saved-successfully' })
        );
        queryCache.invalidateQueries('tendersRegistrationCoreTenders');
        setIsConfirmLoading(false);
        setShowConfirmDelete(false);
      },
    });

    const handleSubmit = async () => {
      if (!props.item.projectId || !props.item.tenderId) return;
      await doUpdateLabel({
        projectId: props.item.projectId,
        tenderId: props.item.tenderId,
        label_id: selectedForDelete,
        value: null,
      });

      setInputValues((prevState) => {
        const newState = { ...prevState };
        delete newState[selectedForDelete];
        return newState;
      });

      setSelectedLabels((prevState) => {
        const newState = { ...prevState };
        Object.keys(newState).forEach((groupId) => {
          newState[groupId] = newState[groupId].filter(
            (id) => id !== selectedForDelete
          );
        });
        return newState;
      });
    };

    return (
      <Modal
        confirmLoading={isConfirmLoading}
        title={
          <ModalTitle
            text={intl.formatMessage({ id: 'delete-label-warning-head' })}
            icon={['far', 'trash']}
          />
        }
        okText={intl.formatMessage({ id: 'delete' })}
        cancelText={intl.formatMessage({ id: 'cancel' })}
        width={420}
        centered
        okButtonProps={{ danger: true }}
        onOk={handleSubmit}
        visible={showConfirmDelete}
        maskClosable={false}
        onCancel={() => setShowConfirmDelete(false)}>
        <Typography.Paragraph css={style.text}>
          {intl.formatMessage({ id: 'delete-label-warning-body' })}
        </Typography.Paragraph>
      </Modal>
    );
  };

  const isLabelSaved = (groupId: number, labelId: number) => {
    const group = props.item.label_values.find(
      (group) => group.group_id === groupId
    );

    if (!group) return false;

    return !!group.labels.find((label) => label.label_id === labelId);
  };

  useEffect(() => {
    Object.keys(selectedLabels).forEach((groupId) => {
      selectedLabels[groupId].forEach((labelId) => {
        setInputValues((prevState) => ({
          ...prevState,
          [labelId]: prevState[labelId] ?? '', // Add default empty string if not already present
        }));
      });
    });
  }, [selectedLabels]);

  return (
    <Modal
      maskClosable={false}
      confirmLoading={isModalLoading}
      title={<Title />}
      visible={props.visible}
      destroyOnClose={true}
      // Expand width when in Settings tab to accommodate the table
      width={activeTab === 'settings' ? 'min(90vw, 800px)' : 520}
      okText={intl.formatMessage({ id: 'save' })}
      onOk={handleSave}
      onCancel={props.onCancel}
      // Hide footer if activeTab is settings, otherwise show default buttons
      footer={activeTab === 'settings' ? null : undefined}>
      <ConfirmDeleteModal />

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ marginTop: -10 }}>
        {/* TAB 1: VALUES (Original Content) */}
        <TabPane tab={intl.formatMessage({ id: 'labels' })} key="values">
          <Row style={{ display: 'flex', flex: 1, marginTop: 10 }}>
            <Col span={6}>
              {props.item.reference ? (
                <Typography>
                  {intl.formatMessage({ id: 'contactor-ref-number' })}
                </Typography>
              ) : null}
              {props.item.title ? (
                <Typography>
                  {intl.formatMessage({ id: 'tender-name' })}
                </Typography>
              ) : null}
            </Col>
            <Col>
              {props.item.reference ? (
                <Typography style={{ fontWeight: 700 }}>
                  {props.item.reference ??
                    intl.formatMessage({ id: 'not-found-id' })}
                </Typography>
              ) : null}
              {props.item.title ? (
                <Typography style={{ fontWeight: 700 }}>
                  {props.item.title?.slice(0, 50) ??
                    intl.formatMessage({ id: 'not-found-title' })}
                </Typography>
              ) : null}
            </Col>
          </Row>

          <div style={{ margin: '12px 0' }}>
            <FieldSelector
              defaultSelected={defaultSelectedLabels}
              selectedGroups={setSelectedLabels}
              selected={selectedLabels}
              data={labelsData ?? []}
            />
          </div>

          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {Object.values(selectedLabels).every((arr) => arr.length === 0) ? (
              <div css={style.emptyBlock}>
                <Typography.Paragraph css={style.text}>
                  {intl.formatMessage({ id: 'no-custom-fields-info-title' })}
                </Typography.Paragraph>
                <Typography.Text type="secondary">
                  {intl.formatMessage({ id: 'no-custom-fields-info-subtext' })}
                </Typography.Text>
              </div>
            ) : (
              labelsData?.map((group) => {
                if (
                  !selectedLabels[group.id] ||
                  !selectedLabels[group.id].length
                )
                  return null;

                return (
                  <Fragment key={group.id}>
                    <label style={{ fontSize: '16px' }}>
                      <b>{group.name}</b>
                    </label>

                    {group.labels.map((label) => {
                      if (!selectedLabels[group.id].includes(label.id))
                        return null;

                      return (
                        <Row
                          key={label.id}
                          style={{
                            display: 'flex',
                            flex: 1,
                            alignItems: 'center',
                            marginTop: '12px',
                          }}>
                          <Col span={10}>
                            <label style={{ fontSize: '14px' }}>
                              {label.title}
                            </label>
                          </Col>
                          <Col span={12}>
                            <Input
                              autoCapitalize="words"
                              value={inputValues[label.id] ?? ''}
                              onChange={(e) => {
                                handleChange(label.id, e.target.value);
                              }}
                            />
                          </Col>
                          <Col span={1}>
                            <IconButton
                              icon={['far', 'circle-xmark']}
                              containerStyle={{
                                marginLeft: 0,
                                color: isLabelSaved(group.id, label.id)
                                  ? '#f65656'
                                  : '#787878',
                                cursor: 'pointer',
                              }}
                              backgroundOff
                              widthTooltip="300px"
                              click={() => {
                                if (isLabelSaved(group.id, label.id)) {
                                  setShowConfirmDelete(true);
                                  setSelectedForDelete(label.id);
                                } else {
                                  setInputValues((prevState) => {
                                    const newState = { ...prevState };
                                    delete newState[label.id];
                                    return newState;
                                  });

                                  setSelectedLabels((prevState) => {
                                    const newState = { ...prevState };
                                    newState[group.id] = newState[
                                      group.id
                                    ].filter((id) => id !== label.id);
                                    return newState;
                                  });
                                }
                              }}
                            />
                          </Col>
                        </Row>
                      );
                    })}
                  </Fragment>
                );
              })
            )}
          </div>
        </TabPane>

        {/* TAB 2: SETTINGS (LabelsTab) */}
        {showSettingsTab && (
          <TabPane
            tab={intl.formatMessage({
              id: 'labels-settings',
              defaultMessage: 'Settings',
            })}
            key="settings">
            {/* Conditional render: only fetch/mount if active */}
            {activeTab === 'settings' && <LabelsTab />}
          </TabPane>
        )}
      </Tabs>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  text: {
    textAlign: 'center',
    margin: 0,
    fontSize: 15,
  },
  emptyBlock: {
    padding: '20px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
});
